import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faCheck, faCreditCard, faUniversity, faInfoCircle, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabase';
import { registrationService, eventService } from '../services/database';
import { PaymentService } from '../services/paymentService';
import {
  isMerchantSelfPayment,
  MERCHANT_SELF_PAYMENT_MESSAGE,
  storePayFastCheckout,
} from '../utils/payfastCheckout';
import { NotificationIntegration } from '../services/notificationIntegration';

interface Minor {
  id: string;
  name: string;
  age: number;
  gender: string;
  category: string;
  medical_conditions?: string;
  allergies?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
}

interface Distance {
  id: string;
  name: string;
  length: string;
  price: string;
  description: string;
  ageRestriction: string;
  difficulty: string;
}

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventTitle: string;
  eventPrice: string;
  distances: Distance[];
  minors: Minor[];
  onRegister: (participants: {id: string, type: 'self' | 'minor', distanceId: string}[], totalPrice: number) => void;
}

interface RegistrationFormData {
  userName: string;
  userEmail: string;
  userPhone: string;
  emergencyContact: string;
  medicalConditions: string;
}

interface PaymentInfo {
  gateway: 'payfast' | 'manual' | 'both';
  availableOptions: ('payfast' | 'manual')[];
  selectedOption?: 'payfast' | 'manual';
  bankingDetails?: {
    bank_name: string;
    account_holder: string;
    account_number: string;
    branch_code: string;
    account_type: string;
    reference_format: string;
  };
}

const RegisterModal: React.FC<RegisterModalProps> = ({ 
  isOpen, 
  onClose, 
  eventId, 
  eventTitle,
  eventPrice,
  distances: initialDistances,
  minors,
  onRegister
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'selection' | 'form' | 'payment' | 'processing'>('selection');
  const [selectedParticipants, setSelectedParticipants] = useState<{id: string, type: 'self' | 'minor', distanceId: string}[]>([]);
  const [formData, setFormData] = useState<RegistrationFormData>({
    userName: '',
    userEmail: '',
    userPhone: '',
    emergencyContact: '',
    medicalConditions: ''
  });
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [distances, setDistances] = useState<Distance[]>(initialDistances);

  const totalPrice = selectedParticipants.reduce((total, participant) => {
    const distance = distances.find(d => d.id === participant.distanceId);
    return total + (distance ? parseFloat(distance.price.replace(/[^0-9.]/g, '')) : 0);
  }, 0);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadCurrentUserData();
      // Fetch latest distances from backend
      eventService.getEventDistances(eventId).then(dbDistances => {
        if (dbDistances && dbDistances.length > 0) {
          setDistances(dbDistances.map(d => ({
            id: d.id,
            name: d.name,
            length: d.length,
            price: d.price.toString(),
            description: d.description || '',
            ageRestriction: d.age_restriction || '',
            difficulty: d.difficulty || ''
          })));
        } else {
          setDistances(initialDistances);
        }
      }).catch(() => setDistances(initialDistances));
    }
  }, [isOpen, eventId]);

  const resetForm = () => {
    setStep('selection');
    setSelectedParticipants([]);
    setFormData({
      userName: '',
      userEmail: '',
      userPhone: '',
      emergencyContact: '',
      medicalConditions: ''
    });
    setRegistrationError(null);
    setPaymentInfo(null);
  };

  const isSelected = (id: string, type: 'self' | 'minor') => {
    return selectedParticipants.some(p => p.id === id && p.type === type);
  };

  const getSelectedDistance = (id: string, type: 'self' | 'minor') => {
    const participant = selectedParticipants.find(p => p.id === id && p.type === type);
    return participant ? participant.distanceId : '';
  };

  const getDefaultDistances = () => [
    { id: 'short', name: 'Short Distance (15km)', length: '15km', price: 'R150', description: 'Perfect for beginners', ageRestriction: '12+', difficulty: 'Easy' },
    { id: 'medium', name: 'Medium Distance (30km)', length: '30km', price: 'R200', description: 'For intermediate riders', ageRestriction: '16+', difficulty: 'Intermediate' },
    { id: 'long', name: 'Long Distance (50km)', length: '50km', price: 'R250', description: 'Challenge for experienced riders', ageRestriction: '18+', difficulty: 'Challenging' }
  ];

  const availableDistances = distances.length > 0 ? distances : getDefaultDistances();

  const toggleSelection = (id: string, type: 'self' | 'minor') => {
    if (isSelected(id, type)) {
      setSelectedParticipants(prev => prev.filter(p => !(p.id === id && p.type === type)));
    } else {
      const firstDistance = availableDistances[0];
      if (firstDistance) {
        setSelectedParticipants(prev => [...prev, {
          id,
          type,
          distanceId: firstDistance.id
        }]);
      }
    }
  };

  const updateDistance = (id: string, type: 'self' | 'minor', distanceId: string) => {
    setSelectedParticipants(prev => prev.map(p => 
      (p.id === id && p.type === type) ? { ...p, distanceId } : p
    ));
  };

  const loadCurrentUserData = async () => {
    try {
      // First try to get user from localStorage (current auth system)
      const currentUserId = localStorage.getItem('gomtb_current_user_id');
      const currentUserEmail = localStorage.getItem('gomtb_current_user_email');
      
      if (currentUserId && currentUserEmail) {
        // Get user data from database using localStorage user ID
        const { data: profile, error } = await supabase
          .from('users')
          .select('name, email, phone, emergency_contact_name, emergency_contact_phone, emergency_contact_relationship, medical_conditions, allergies')
          .eq('id', currentUserId)
          .single();

        if (profile && !error) {
          // Construct emergency contact string (name + phone + relationship if available)
          let emergencyContact = '';
          if (profile.emergency_contact_name && profile.emergency_contact_phone) {
            emergencyContact = `${profile.emergency_contact_name} - ${profile.emergency_contact_phone}`;
            if (profile.emergency_contact_relationship) {
              emergencyContact += ` (${profile.emergency_contact_relationship})`;
            }
          }

          // Construct medical conditions string
          let medicalInfo = '';
          const allergies = profile.allergies && profile.allergies !== 'None' ? profile.allergies : '';
          const conditions = profile.medical_conditions && profile.medical_conditions !== 'None' ? profile.medical_conditions : '';
          
          if (allergies && conditions) {
            medicalInfo = `Allergies: ${allergies}. Medical Conditions: ${conditions}`;
          } else if (allergies) {
            medicalInfo = `Allergies: ${allergies}`;
          } else if (conditions) {
            medicalInfo = `Medical Conditions: ${conditions}`;
          }

          setFormData(prev => ({
            ...prev,
            userName: profile.name || '',
            userEmail: profile.email || '',
            userPhone: profile.phone || '',
            emergencyContact: emergencyContact,
            medicalConditions: medicalInfo
          }));
          return;
        }
      }
      
      // Fallback: Try Supabase Auth if localStorage method didn't work
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile, error } = await supabase
          .from('users')
          .select('name, email, phone, emergency_contact_name, emergency_contact_phone, emergency_contact_relationship, medical_conditions, allergies')
          .eq('id', user.id)
          .single();

        if (profile && !error) {
          // Same logic as above for emergency contact and medical info
          let emergencyContact = '';
          if (profile.emergency_contact_name && profile.emergency_contact_phone) {
            emergencyContact = `${profile.emergency_contact_name} - ${profile.emergency_contact_phone}`;
            if (profile.emergency_contact_relationship) {
              emergencyContact += ` (${profile.emergency_contact_relationship})`;
            }
          }

          let medicalInfo = '';
          const allergies = profile.allergies && profile.allergies !== 'None' ? profile.allergies : '';
          const conditions = profile.medical_conditions && profile.medical_conditions !== 'None' ? profile.medical_conditions : '';
          
          if (allergies && conditions) {
            medicalInfo = `Allergies: ${allergies}. Medical Conditions: ${conditions}`;
          } else if (allergies) {
            medicalInfo = `Allergies: ${allergies}`;
          } else if (conditions) {
            medicalInfo = `Medical Conditions: ${conditions}`;
          }

          setFormData(prev => ({
            ...prev,
            userName: profile.name || '',
            userEmail: profile.email || '',
            userPhone: profile.phone || '',
            emergencyContact: emergencyContact,
            medicalConditions: medicalInfo
          }));
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleContinueToForm = () => {
    if (selectedParticipants.length === 0) {
      setRegistrationError('Please select at least one participant.');
      return;
    }
    setRegistrationError(null);
    setStep('form');
  };

  // Load payment info with proper admin support
  const loadPaymentInfo = async () => {
    console.log('🔄 RegisterModal.loadPaymentInfo called');
    try {
      // Get event organizer information
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('organizer_id')
        .eq('id', eventId)
        .single();
        
      console.log('Event query result:', { event, eventError });

      if (eventError || !event) {
        console.error('Failed to load event info:', eventError);
        return;
      }

      // Use PaymentService to get the organizer config - this handles admin events properly
      console.log('Calling PaymentService.getOrganizerConfig with:', event.organizer_id);
      const organizerConfig = await PaymentService.getOrganizerConfig(event.organizer_id);
      console.log('PaymentService returned organizerConfig:', organizerConfig);
      
      if (organizerConfig) {
        console.log('Found payment configuration:', organizerConfig);
        
        // Use the payment configuration
        const availableOptions: ('payfast' | 'manual')[] = [];
        
        // Handle different payment gateway types
        if (organizerConfig.payment_gateway === 'payfast') {
          availableOptions.push('payfast');
        } else if (organizerConfig.payment_gateway === 'manual') {
          availableOptions.push('manual');
        } else if (organizerConfig.payment_gateway === 'paypal' || organizerConfig.payment_gateway === 'stripe') {
          // For now, treat paypal and stripe as manual payments until implemented
          availableOptions.push('manual');
        }

        // Ensure we always have at least one payment option
        if (availableOptions.length === 0) {
          availableOptions.push('manual');
        }

        // Map the payment gateway to the expected type in PaymentInfo
        let mappedGateway: 'payfast' | 'manual' | 'both' = 'manual';
        if (organizerConfig.payment_gateway === 'payfast') {
          mappedGateway = 'payfast';
        } else if (availableOptions.includes('payfast') && availableOptions.includes('manual')) {
          mappedGateway = 'both';
        }
        
        const paymentInfo: PaymentInfo = {
          gateway: mappedGateway,
          availableOptions,
          selectedOption: availableOptions[0], // Default to first available option
          bankingDetails: organizerConfig.payment_config?.banking_details ? {
            bank_name: organizerConfig.payment_config.banking_details.bank_name,
            account_holder: organizerConfig.payment_config.banking_details.account_holder,
            account_number: organizerConfig.payment_config.banking_details.account_number,
            branch_code: organizerConfig.payment_config.banking_details.branch_code,
            account_type: organizerConfig.payment_config.banking_details.account_type || 'Cheque',
            reference_format: organizerConfig.payment_config.banking_details.reference_format || 'Use registration reference'
          } : undefined
        };

        setPaymentInfo(paymentInfo);
        return;
      }

      console.log('No event organizer record found, checking coordinator payment config...');

      // FALLBACK: If no event organizer record found, check coordinator payment config
      const { data: organizer, error: organizerError } = await supabase
        .from('users')
        .select('email')
        .eq('id', event.organizer_id)
        .single();

      if (organizerError || !organizer) {
        console.error('Failed to load organizer info:', organizerError);
        return;
      }

      // Get coordinator payment configuration
      const { data: paymentConfig, error: configError } = await supabase
        .from('coordinator_payment_configs')
        .select('*')
        .eq('coordinator_email', organizer.email)
        .eq('is_active', true)
        .single();

      if (configError && configError.code !== 'PGRST116') {
        console.error('Failed to load payment config:', configError);
        return;
      }

      let paymentInfo: PaymentInfo;

      if (paymentConfig) {
        console.log('Found coordinator payment config:', paymentConfig);
        
        // Use coordinator's payment configuration
        const availableOptions: ('payfast' | 'manual')[] = [];
        
        if (paymentConfig.payment_gateway === 'payfast') {
          availableOptions.push('payfast');
        } else if (paymentConfig.payment_gateway === 'manual') {
          availableOptions.push('manual');
        } else if (paymentConfig.payment_gateway === 'both') {
          availableOptions.push('payfast', 'manual');
        }

        paymentInfo = {
          gateway: paymentConfig.payment_gateway,
          availableOptions,
          selectedOption: availableOptions[0], // Default to first available option
          bankingDetails: paymentConfig.bank_name ? {
            bank_name: paymentConfig.bank_name,
            account_holder: paymentConfig.account_holder,
            account_number: paymentConfig.account_number,
            branch_code: paymentConfig.branch_code,
            account_type: paymentConfig.account_type || 'Cheque',
            reference_format: paymentConfig.payment_instructions || 'Use registration reference'
          } : undefined
        };
      } else {
        console.log('No payment configuration found, falling back to manual payment');
        
        // Fallback to manual payment if no configuration found
        paymentInfo = {
          gateway: 'manual',
          availableOptions: ['manual'],
          selectedOption: 'manual'
        };
      }

      setPaymentInfo(paymentInfo);
    } catch (error) {
      console.error('Error loading payment info:', error);
      // Fallback to manual payment
      setPaymentInfo({
        gateway: 'manual',
        availableOptions: ['manual'],
        selectedOption: 'manual'
      });
    }
  };

  const handleSubmitRegistration = async () => {
    if (!formData.userName || !formData.userEmail || !formData.userPhone || !formData.emergencyContact) {
      setRegistrationError('Please fill in all required fields.');
      return;
    }

    try {
      setRegistrationError(null);

      // Check if the specific participants being registered are already registered for this event
      const existingRegistrations = await registrationService.getExistingRegistrations(eventId, formData.userEmail);
      
      if (existingRegistrations && existingRegistrations.length > 0) {
        // Check if any of the selected participants are already registered
        const alreadyRegisteredParticipants = [];
        
        for (const participant of selectedParticipants) {
          // Get participant name
          const participantName = participant.type === 'self' 
            ? formData.userName 
            : minors?.find(m => m.id === participant.id)?.name || 'Unknown';
          
          // Check if this specific participant is already registered
          const isAlreadyRegistered = existingRegistrations.some(reg => 
            reg.user_name === participantName
          );
          
          if (isAlreadyRegistered) {
            alreadyRegisteredParticipants.push(participantName);
          }
        }
        
        if (alreadyRegisteredParticipants.length > 0) {
          const names = alreadyRegisteredParticipants.join(', ');
          setRegistrationError(`The following participants are already registered for this event: ${names}`);
          return;
        }
        
        // If we get here, none of the selected participants are already registered
        console.log('Account has existing registrations but selected participants are different');
      }

      // Load payment information and proceed to payment step
      await loadPaymentInfo();
      setStep('payment');

    } catch (error) {
      console.error('Registration error:', error);
      setRegistrationError('Failed to proceed to payment. Please try again.');
    }
  };

  const createRegistrationRecords = async (paymentReference: string, paymentMethod: 'payfast' | 'manual', initialStatus: 'pending' | 'confirmed' = 'pending') => {
    try {
      // Log the selected participants to verify who's being registered
      console.log('Selected participants for registration:', selectedParticipants);
      console.log(`Creating registration records with status: ${initialStatus}, payment method: ${paymentMethod}`);
      
      // Only create registration records for the actual selected participants
      const registrationPromises = selectedParticipants.map(async (participant) => {
        let participantName, emergencyContact, medicalConditions;
        
        if (participant.type === 'self') {
          // This is the account holder registering themselves
          participantName = formData.userName;
          emergencyContact = formData.emergencyContact;
          medicalConditions = formData.medicalConditions || 'None';
          console.log(`Creating registration for account holder: ${participantName}`);
        } else {
          // This is a minor/dependent - find their specific information
          const minor = minors?.find(m => m.id === participant.id);
          participantName = minor?.name || 'Unknown';
          
          // Use minor-specific emergency contact if available in database
          if (minor?.emergency_contact_name && minor?.emergency_contact_phone) {
            emergencyContact = `${minor.emergency_contact_name} - ${minor.emergency_contact_phone}`;
            if (minor.emergency_contact_relationship) {
              emergencyContact += ` (${minor.emergency_contact_relationship})`;
            }
          } else {
            // Check localStorage for legacy data (for existing minors created before schema update)
            const legacyData = localStorage.getItem(`minor_additional_${minor?.name}`);
            if (legacyData) {
              try {
                const parsedData = JSON.parse(legacyData);
                if (parsedData.guardian_name && parsedData.guardian_phone) {
                  emergencyContact = `${parsedData.guardian_name} - ${parsedData.guardian_phone}`;
                  if (parsedData.guardian_relationship) {
                    emergencyContact += ` (${parsedData.guardian_relationship})`;
                  }
                } else {
                  emergencyContact = formData.emergencyContact;
                }
              } catch (e) {
                emergencyContact = formData.emergencyContact;
              }
            } else {
              // Fall back to account holder's emergency contact
              emergencyContact = formData.emergencyContact;
            }
          }
          
          // Use minor-specific medical information if available
          let minorMedicalInfo = '';
          
          // Check database first
          const allergies = minor?.allergies && minor.allergies !== 'None' ? minor.allergies : '';
          const conditions = minor?.medical_conditions && minor.medical_conditions !== 'None' ? minor.medical_conditions : '';
          
          if (allergies || conditions) {
            if (allergies && conditions) {
              minorMedicalInfo = `Allergies: ${allergies}. Medical Conditions: ${conditions}`;
            } else if (allergies) {
              minorMedicalInfo = `Allergies: ${allergies}`;
            } else if (conditions) {
              minorMedicalInfo = `Medical Conditions: ${conditions}`;
            }
          } else {
            // Check localStorage for legacy data (for existing minors created before schema update)
            const legacyData = localStorage.getItem(`minor_additional_${minor?.name}`);
            if (legacyData) {
              try {
                const parsedData = JSON.parse(legacyData);
                const legacyAllergies = parsedData.allergies && parsedData.allergies !== 'None' ? parsedData.allergies : '';
                const legacyConditions = parsedData.medical_conditions && parsedData.medical_conditions !== 'None' ? parsedData.medical_conditions : '';
                
                if (legacyAllergies && legacyConditions) {
                  minorMedicalInfo = `Allergies: ${legacyAllergies}. Medical Conditions: ${legacyConditions}`;
                } else if (legacyAllergies) {
                  minorMedicalInfo = `Allergies: ${legacyAllergies}`;
                } else if (legacyConditions) {
                  minorMedicalInfo = `Medical Conditions: ${legacyConditions}`;
                }
              } catch (e) {
                // Ignore localStorage parsing errors
              }
            }
          }
          
          // Use minor's medical info if available, otherwise fall back to account holder's
          medicalConditions = minorMedicalInfo || formData.medicalConditions || 'None';
          
          console.log(`Creating registration for minor: ${participantName}, with specific medical/emergency info:`, 
            { hasMinorMedicalInfo: !!minorMedicalInfo, hasMinorEmergencyContact: !!(minor?.emergency_contact_name) });
        }
        
        const distance = distances.find(d => d.id === participant.distanceId);
        const participantPrice = distance ? parseFloat(distance.price.replace(/[^0-9.]/g, '')) : 0;

        // Always create with pending status initially for PayFast
        // Only mark as confirmed for manual payments or after PayFast callback
        const status = initialStatus;
        const paymentStatus = initialStatus === 'confirmed' ? 'completed' : 'pending';
        
        // Create a registration record for this specific participant with their specific information
        return registrationService.createRegistration({
          event_id: eventId,
          user_name: participantName, // The actual participant name
          user_email: formData.userEmail, // Contact email is always the account holder's
          user_phone: formData.userPhone, // Contact phone is always the account holder's
          emergency_contact: emergencyContact, // Participant-specific emergency contact
          medical_conditions: medicalConditions, // Participant-specific medical conditions
          status: status,
          payment_status: paymentStatus,
          payment_method: paymentMethod,
          payment_reference: paymentReference,
          payment_amount: participantPrice,
          payment_date: initialStatus === 'confirmed' ? new Date().toISOString() : null,
          distance_id: participant.distanceId
          // We can track if this is the account holder through the user_name matching formData.userName
        });
      });

      const registrations = await Promise.all(registrationPromises);
      console.log('Registration records created:', registrations.length);
      console.log('Registration details:', registrations.map(r => ({
        name: r.user_name,
        is_self_registration: r.user_name === formData.userName, // Check if this is the account holder
        distance_id: r.distance_id,
        has_specific_medical_info: r.medical_conditions !== 'None'
      })));
      
      return registrations[0];
    } catch (error) {
      console.error('Error creating registration records:', error);
      throw error;
    }
  };

  const showManualPaymentInstructions = (paymentReference: string) => {
    let instructions = `
Registration Confirmed for ${eventTitle}!

PAYMENT INSTRUCTIONS:
Amount: R${totalPrice.toFixed(2)}
Reference: ${paymentReference}
`;

    if (paymentInfo?.bankingDetails) {
      instructions += `
BANKING DETAILS:
Bank: ${paymentInfo.bankingDetails.bank_name}
Account Holder: ${paymentInfo.bankingDetails.account_holder}
Account Number: ${paymentInfo.bankingDetails.account_number}
Branch Code: ${paymentInfo.bankingDetails.branch_code}
Account Type: ${paymentInfo.bankingDetails.account_type}

${paymentInfo.bankingDetails.reference_format}
`;
    } else {
      instructions += `
Please contact the organizer for payment details.
`;
    }

    instructions += `
Your registration will be confirmed once payment is received.`;

    alert(instructions);
    
    NotificationIntegration.onEventRegistration(eventTitle, new Date().toISOString(), eventId);
    onRegister(selectedParticipants, totalPrice);
    resetForm();
    onClose();
  };

  const handleProcessPayment = async () => {
    if (!paymentInfo?.selectedOption) {
      setRegistrationError('Please select a payment method.');
      return;
    }

    setStep('processing');
    setRegistrationError(null);

    try {
      // Generate a UUID for the payment reference
      let paymentReference: string;
      try {
        // Try the native crypto.randomUUID() if available
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          paymentReference = crypto.randomUUID();
        } else {
          // Fallback to a UUID v4 generation algorithm
          paymentReference = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        }
        console.log('Generated payment reference UUID:', paymentReference);
      } catch (error) {
        console.error('Error generating payment reference UUID:', error);
        // Use a timestamp-based ID as last resort
        paymentReference = `reg-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      }

      if (paymentInfo.selectedOption === 'payfast') {
        // PayFast rejects payments from the merchant's own account email — catch it
        // before creating a pending registration record
        if (isMerchantSelfPayment(formData.userEmail)) {
          setRegistrationError(MERCHANT_SELF_PAYMENT_MESSAGE);
          setStep('payment');
          return;
        }

        try {
          // First create the registration records with PENDING status
          console.log('Creating registration records with payment reference:', paymentReference);
          const registration = await createRegistrationRecords(paymentReference, 'payfast', 'pending');
          
          if (!registration) {
            throw new Error('Failed to create registration records');
          }
          
          console.log('Registration created successfully with ID:', registration.id);
          
          // Get the event's organizer ID from the event data we loaded earlier
          const { data: event } = await supabase
            .from('events')
            .select('organizer_id')
            .eq('id', eventId)
            .single();
          
          if (!event || !event.organizer_id) {
            throw new Error('Could not determine event organizer');
          }
          
          console.log('Using organizer_id for payment:', event.organizer_id);
          
          // Resolve user id from registration email (guest checkout supported)
          let userId = '';
          const { data: userRow } = await supabase
            .from('users')
            .select('id')
            .eq('email', formData.userEmail)
            .maybeSingle();
          if (userRow) {
            userId = userRow.id;
          }

          const paymentRequest = {
            registrationId: registration.id,
            eventId: eventId,
            organizerId: event.organizer_id,
            amount: totalPrice,
            currency: 'ZAR',
            userEmail: formData.userEmail,
            userName: formData.userName,
            userId,
            eventTitle,
          };

          // Store registration ID in session storage for potential status update after payment
          try {
            sessionStorage.setItem('pendingRegistrationId', registration.id);
            sessionStorage.setItem('pendingPaymentReference', paymentReference);
          } catch (e) {
            console.warn('Could not store registration data in session storage:', e);
          }

          const paymentResponse = await PaymentService.processPayment(paymentRequest);

          if (!paymentResponse.success) {
            console.error('Payment failed:', paymentResponse.error);
            setRegistrationError(paymentResponse.error || 'Payment processing failed');
            setStep('payment');
            if (registration?.id) {
              await PaymentService.updatePaymentStatus(registration.id, 'failed');
            }
            return;
          }

          if (paymentResponse.payfastCheckout) {
            storePayFastCheckout(paymentResponse.payfastCheckout);
            // Client-side navigation keeps the same JS bundle running (avoids
            // a full page load that could serve a stale cached index.html)
            navigate('/payfast-checkout');
            return;
          }
        } catch (error) {
          console.error('Error in PayFast payment flow:', error);
          const message = error instanceof Error ? error.message : 'Payment processing failed. Please try again.';
          setRegistrationError(message);
          setStep('payment');
        }
      } else {
        // For manual payments, we create with pending status
        await createRegistrationRecords(paymentReference, 'manual', 'pending');
        showManualPaymentInstructions(paymentReference);
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      const message = error instanceof Error ? error.message : 'Payment processing failed. Please try again.';
      setRegistrationError(message);
      setStep('payment');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Register for {eventTitle}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          {registrationError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {registrationError}
            </div>
          )}
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 'selection' && (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Select Participants</h3>
                
                <div className="space-y-3">
                  <div className={`border rounded-lg p-3 cursor-pointer ${isSelected('self', 'self') ? 'border-primary bg-orange-50' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between" onClick={() => toggleSelection('self', 'self')}>
                      <div className="flex items-center">
                        <div className={`w-5 h-5 rounded border ${isSelected('self', 'self') ? 'bg-primary border-primary' : 'border-gray-300'} flex items-center justify-center mr-3`}>
                          {isSelected('self', 'self') && <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />}
                        </div>
                        <div>
                          <p className="font-medium">Yourself</p>
                          <p className="text-sm text-gray-500">Primary registration</p>
                        </div>
                      </div>
                    </div>
                    
                    {isSelected('self', 'self') && (
                      <div className="mt-3 pt-3 border-t">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Distance</label>
                        <select
                          value={getSelectedDistance('self', 'self')}
                          onChange={(e) => updateDistance('self', 'self', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded focus:ring-primary focus:border-primary"
                        >
                          {availableDistances.map(distance => (
                            <option key={distance.id} value={distance.id}>
                              {distance.name} - {distance.price}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {minors && minors.length > 0 && (
                    <>
                      <h4 className="font-medium text-gray-700 mt-6 mb-3">Register Minors</h4>
                      {minors.map(minor => (
                        <div key={minor.id} className={`border rounded-lg p-3 cursor-pointer ${isSelected(minor.id, 'minor') ? 'border-primary bg-orange-50' : 'border-gray-200'}`}>
                          <div className="flex items-center justify-between" onClick={() => toggleSelection(minor.id, 'minor')}>
                            <div className="flex items-center">
                              <div className={`w-5 h-5 rounded border ${isSelected(minor.id, 'minor') ? 'bg-primary border-primary' : 'border-gray-300'} flex items-center justify-center mr-3`}>
                                {isSelected(minor.id, 'minor') && <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />}
                              </div>
                              <div>
                                <p className="font-medium">{minor.name}</p>
                                <p className="text-sm text-gray-500">{minor.age} years • {minor.category}</p>
                              </div>
                            </div>
                          </div>
                          
                          {isSelected(minor.id, 'minor') && (
                            <div className="mt-3 pt-3 border-t">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Select Distance</label>
                              <select
                                value={getSelectedDistance(minor.id, 'minor')}
                                onChange={(e) => updateDistance(minor.id, 'minor', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-primary focus:border-primary"
                              >
                                {availableDistances.map(distance => (
                                  <option key={distance.id} value={distance.id}>
                                    {distance.name} - {distance.price}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 'form' && (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Registration Details</h3>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="userName"
                      value={formData.userName}
                      onChange={(e) => setFormData({...formData, userName: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="userEmail"
                      value={formData.userEmail}
                      onChange={(e) => setFormData({...formData, userEmail: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="userPhone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="userPhone"
                      value={formData.userPhone}
                      onChange={(e) => setFormData({...formData, userPhone: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="emergencyContact" className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Contact *
                    </label>
                    <input
                      type="text"
                      id="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={(e) => setFormData({...formData, emergencyContact: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      placeholder="Emergency contact name and phone number"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="medicalConditions" className="block text-sm font-medium text-gray-700 mb-1">
                      Medical Conditions
                    </label>
                    <textarea
                      id="medicalConditions"
                      value={formData.medicalConditions}
                      onChange={(e) => setFormData({...formData, medicalConditions: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      placeholder="Any medical conditions we should know about (optional)"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Registration Summary</h3>
                <div className="space-y-2">
                  {selectedParticipants.map(participant => {
                    const distance = distances.find(d => d.id === participant.distanceId);
                    const participantName = participant.type === 'self' 
                      ? 'Yourself' 
                      : minors?.find(m => m.id === participant.id)?.name || 'Unknown';
                    
                    return (
                      <div key={`${participant.id}-${participant.type}`} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{participantName}</p>
                          <p className="text-sm text-gray-600">
                            {distance?.name} ({distance?.length})
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{distance?.price}</p>
                        </div>
                      </div>
                    );
                  })}
                  
                  <div className="flex justify-between items-center p-3 bg-primary text-white rounded-lg font-medium">
                    <span>Total Amount</span>
                    <span>R{totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 'payment' && (
            <>
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  Select a payment method for <span className="font-medium">{eventTitle}</span>
                </p>
                <div className="text-sm text-gray-500">
                  Selected: {selectedParticipants.length} participant(s) • Total: R{totalPrice.toFixed(2)}
                </div>
              </div>

              <div className="space-y-3">
                {paymentInfo?.availableOptions.includes('payfast') && (
                  <div 
                    className={`border rounded-lg p-3 cursor-pointer hover:bg-gray-50 ${paymentInfo?.selectedOption === 'payfast' ? 'border-primary bg-orange-50' : ''}`}
                    onClick={() => setPaymentInfo({...paymentInfo, selectedOption: 'payfast'})}
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 mr-3">
                        <div className={`w-6 h-6 rounded-md border ${paymentInfo?.selectedOption === 'payfast' ? 'bg-primary border-primary' : 'border-gray-300'} flex items-center justify-center`}>
                          {paymentInfo?.selectedOption === 'payfast' && <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <p className="font-medium">PayFast</p>
                        <p className="text-sm text-gray-500">Online payment - instant confirmation</p>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        <FontAwesomeIcon icon={faCreditCard} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                )}

                {paymentInfo?.availableOptions.includes('manual') && (
                  <div 
                    className={`border rounded-lg p-3 cursor-pointer hover:bg-gray-50 ${paymentInfo?.selectedOption === 'manual' ? 'border-primary bg-orange-50' : ''}`}
                    onClick={() => setPaymentInfo({...paymentInfo, selectedOption: 'manual'})}
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 mr-3">
                        <div className={`w-6 h-6 rounded-md border ${paymentInfo?.selectedOption === 'manual' ? 'bg-primary border-primary' : 'border-gray-300'} flex items-center justify-center`}>
                          {paymentInfo?.selectedOption === 'manual' && <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <p className="font-medium">Bank Transfer</p>
                        <p className="text-sm text-gray-500">Manual payment - requires verification</p>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        <FontAwesomeIcon icon={faUniversity} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                )}

                {(!paymentInfo?.availableOptions || paymentInfo.availableOptions.length === 0) && (
                  <div className="text-center py-4 text-gray-500">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-2xl mb-2" />
                    <p>No payment methods configured for this event.</p>
                    <p className="text-sm">Please contact the organizer.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {step === 'processing' && (
            <div className="text-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin mb-4" />
              <p className="text-lg font-medium">Processing your registration...</p>
              <p className="text-gray-500">Please wait while we save your information.</p>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t bg-gray-50 sticky bottom-0">
          {step === 'selection' && (
            <>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="text-sm text-gray-500">Selected participants:</p>
                  <p className="font-medium">{selectedParticipants.length}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total:</p>
                  <p className="font-medium text-lg">R{totalPrice.toFixed(2)}</p>
                </div>
              </div>
              
              <button 
                className={`w-full py-3 rounded-lg flex items-center justify-center ${selectedParticipants.length > 0 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                onClick={handleContinueToForm}
                disabled={selectedParticipants.length === 0}
              >
                Continue to Registration Form
              </button>
            </>
          )}

          {step === 'form' && (
            <div className="flex gap-3">
              <button 
                className="w-20 py-3 rounded-lg border border-gray-300 text-gray-700 text-sm"
                onClick={() => setStep('selection')}
              >
                Back
              </button>
              <button 
                className="flex-1 py-3 rounded-lg bg-primary text-white flex items-center justify-center"
                onClick={handleSubmitRegistration}
              >
                <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                Continue to Payment
              </button>
            </div>
          )}
          
          {step === 'payment' && (
            <div className="flex gap-3">
              <button 
                className="w-20 py-3 rounded-lg border border-gray-300 text-gray-700 text-sm"
                onClick={() => setStep('form')}
              >
                Back
              </button>
              <button 
                className="flex-1 py-3 rounded-lg bg-primary text-white flex items-center justify-center"
                onClick={handleProcessPayment}
              >
                <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                Complete Registration • R{totalPrice.toFixed(2)}
              </button>
            </div>
          )}
          
          {step !== 'processing' && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              By registering, you agree to the event terms and conditions.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterModal; 